import React, { useContext, useState } from "react";
import { AuthContext } from "../../utils/context/auth";
import { FaRegEdit, FaRedo } from "react-icons/fa";
import { IoMailOutline, IoLocationOutline, IoCallOutline, IoCalendarOutline, IoCheckmarkCircle, IoCloseCircle, IoBriefcaseOutline, IoBusinessOutline } from "react-icons/io5";
import axiosAuth from "../../utils/axios";
import ProviderProfileEdit from "./ProviderProfileEdit";
import { Modal, Box } from "@mui/material";
import toast from "react-hot-toast";

const ProviderProfileCard = () => {
  const { user, updateUserProfile } = useContext(AuthContext);
  const [profile, setProfile] = useState(user); 
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isResettingTour, setIsResettingTour] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async (updatedData) => {
  setSaving(true);
  setError(null);
  try {
    const axiosInstance = axiosAuth();
    const response = await axiosInstance.put("/provider/profile/", updatedData);
    setProfile(response.data); 
    updateUserProfile(response.data); // ✅ Add this line to update AuthContext
    setIsEditing(false);
    toast.success('Profile updated successfully!');
  } catch (err) {
    console.error("Failed to update profile:", err);
    setError(err.response?.data?.detail || "Failed to save profile. Please try again.");
    toast.error("Failed to update profile");
  } finally {
    setSaving(false);
  }
};

  const handleRestartTour = async () => {
    try {
      setIsResettingTour(true);
      
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.put("/provider/reset-tour/");
      
      if (response.data.success) {
        toast.success("Tour will restart when you return to the dashboard!", {
          duration: 2000,
          icon: "🎉"
        });
        
        setTimeout(() => {
          window.location.href = "/dashboard/";
        }, 1500);
      }
    } catch (error) {
      console.error("Error resetting tour:", error);
      toast.error("Failed to reset tour. Please try again.");
      setIsResettingTour(false);
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-gray-400 dark:text-gray-600">
        <div className="animate-pulse">Loading User Data...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-red-500">
        Profile not found.
      </div>
    );
  }
  
  const profileCreatedDate = profile.date_created ? new Date(profile.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "N/A";
  
  const formatUSPhoneNumber = (number) => {
    if (!number || typeof number !== "string") return "N/A";
    const cleanNumber = number.replace(/\D/g, "");
    if (cleanNumber.length === 10) {
      return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(3, 6)}-${cleanNumber.substring(6, 10)}`;
    }
    if (cleanNumber.startsWith('1') && cleanNumber.length === 11) {
        return `+1 (${cleanNumber.substring(1, 4)}) ${cleanNumber.substring(4, 7)}-${cleanNumber.substring(7, 11)}`;
    }
    return number;
  };

  const InfoCard = ({ icon: Icon, label, value, iconColor = "text-teal-500" }) => (
    <div className="flex items-start space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:shadow-sm transition-all duration-200">
      <div className={`${iconColor} p-2 rounded-lg`}>
        <Icon className="text-xl" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
          {label}
        </p>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
          {value || "Not specified"}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Profile Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden mb-6 border border-gray-200 dark:border-gray-700">
          
          {/* Gradient Banner */}
          <div className="h-32 bg-gradient-to-r from-teal-500 to-teal-600"></div>
          
          {/* Profile Content */}
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 -mt-16">
              
              {/* Profile Image with Edit Button */}
              <div className="relative flex-shrink-0">
                <img
                  src={
                    profile.image && profile.image.startsWith("http")
                      ? profile.image
                      : profile.image 
                        ? `${process.env.REACT_APP_MEDIA_URL || ''}${profile.image}` 
                        : "https://promedheatlhdatastorage.blob.core.windows.net/static/images/default_user.jpg"
                  }
                  alt="Profile"
                  className="w-28 h-28 rounded-2xl border-4 border-white dark:border-gray-800 shadow-xl object-cover"
                />
                <button
                  onClick={handleEdit}
                  className="absolute -bottom-2 -right-2 p-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl shadow-lg transition-all duration-200 hover:scale-110"
                  title="Edit Profile"
                >
                  <FaRegEdit className="text-sm" />
                </button>
              </div>
              
              {/* User Information */}
              <div className="flex-1 text-center sm:text-left pt-4 sm:pt-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {profile.full_name || profile.user?.full_name || "New Provider"}
                </h1>
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <IoMailOutline className="text-lg text-teal-500" />
                    <span className="text-sm">{profile.user?.email || "No Email"}</span>
                  </div>
                  <span className="hidden sm:inline text-gray-300 dark:text-gray-600">•</span>
                  <div className="flex items-center gap-2">
                    <IoCalendarOutline className="text-lg text-teal-500" />
                    <span className="text-sm">Member since {profileCreatedDate}</span>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Contact & Location */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                Contact & Location
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard 
                  icon={IoLocationOutline} 
                  label="Location" 
                  value={`${profile.city || 'N/A'}, ${profile.country || 'N/A'}`} 
                />
                <InfoCard 
                  icon={IoCallOutline} 
                  label="Facility Phone" 
                  value={formatUSPhoneNumber(profile.facility_phone_number)} 
                />
              </div>
            </div>

            {/* Facility Details */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                Facility Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard 
                  icon={IoBriefcaseOutline} 
                  label="Role" 
                  value={profile.role} 
                  iconColor="text-purple-500"
                />
                <InfoCard 
                  icon={IoBusinessOutline} 
                  label="Facility" 
                  value={profile.facility} 
                  iconColor="text-blue-500"
                />
              </div>
            </div>

          </div>

          {/* Right Column - Dashboard Tour */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 sticky top-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <FaRedo className="text-teal-600 dark:text-teal-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Dashboard Tour
                </h2>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Need a refresher? Restart the guided tour to learn about patient management, document uploads, and order tracking.
              </p>
              
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg mb-4 ${
                user?.has_completed_tour 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                  : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
              }`}>
                {user?.has_completed_tour ? (
                  <>
                    <IoCheckmarkCircle className="text-lg" />
                    <span className="text-sm font-medium">Tour Completed</span>
                  </>
                ) : (
                  <>
                    <IoCloseCircle className="text-lg" />
                    <span className="text-sm font-medium">Tour Not Completed</span>
                  </>
                )}
              </div>
              
              <button
                onClick={handleRestartTour}
                disabled={isResettingTour}
                className="w-full px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-xl shadow-sm hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isResettingTour ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Restarting...</span>
                  </>
                ) : (
                  <>
                    <FaRedo className="text-sm" />
                    <span>Restart Tour</span>
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>
      
      {/* Modal for Editing */}
      <Modal open={isEditing} onClose={handleCancelEdit}>
        <Box 
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '90%', sm: 600 },
            p: 0, 
            outline: 0, 
            bgcolor: 'transparent', 
            border: 'none',
          }}
        >
          <ProviderProfileEdit
            profile={profile}
            onSave={handleSave}
            onCancel={handleCancelEdit}
            isLoading={saving}
            error={error}
          />
        </Box>
      </Modal>
    </div>
  );
};

export default ProviderProfileCard;